import { useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { I18n } from '@/i18n/I18n';
import { useRequirementsStore } from '@/frontend/store/RequirementsStore';
import { RequirementSubjectType } from '@/backend/access/requirement/types';

interface CreateRequirementModalProps {
  subjectId: string;
  subjectType: RequirementSubjectType;
}

export function CreateRequirementModal({ subjectId, subjectType }: CreateRequirementModalProps) {
  const [show, setShow] = useState(false);
  const [name, setName] = useState('');
  const [identifier, setIdentifier] = useState('');
  const [description, setDescription] = useState('');
  const [nameError, setNameError] = useState('');
  
  const createRequirement = useRequirementsStore(state => state.createRequirement);

  const handleClose = () => {
    setShow(false);
    clearForm();
  };
  
  const handleShow = () => {
    setNameError('');
    setShow(true);
  };

  const clearForm = () => {
    setName('');
    setIdentifier('');
    setDescription('');
    setNameError('');
  };

  const handleSubmit = async () => {
    let hasError = false;
    
    if (!name) {
      setNameError(I18n.get('requiredFieldError', { field: I18n.get('title') }));
      hasError = true;
    }

    if (hasError) {
      return;
    }

    await createRequirement({
      name,
      type: 'template',
      description,
      subjectId,
      subjectType,
      identifier: identifier.trim() || undefined,
      applicable: true,
    });
    
    handleClose();
  };

  return (
    <>
      <Button variant="primary" size="sm" onClick={handleShow}>
        {I18n.get('createModel', { model: I18n.get('requirementUppercase') })}
      </Button>

      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title>{I18n.get('createModel', { model: I18n.get('requirementUppercase') })}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label htmlFor="requirement-name">{I18n.get('title')}</Form.Label>
              <Form.Control
                id="requirement-name"
                type="text"
                value={name}
                placeholder={I18n.get('enterField', { field: I18n.get('title') })}
                aria-label={I18n.get('enterField', { field: I18n.get('title') })}
                isInvalid={!!nameError}
                onChange={(e) => {
                  setName(e.target.value);
                  setNameError('');
                }}
              />
              <Form.Control.Feedback type="invalid">{nameError}</Form.Control.Feedback>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label htmlFor="requirement-identifier" className="text-body-secondary small">
                {I18n.get('identifier')} ({I18n.get('optional')})
              </Form.Label>
              <Form.Control
                id="requirement-identifier"
                type="text"
                value={identifier}
                placeholder={I18n.get('enterField', { field: I18n.get('identifier') })}
                aria-label={I18n.get('enterField', { field: I18n.get('identifier') })}
                onChange={(e) => setIdentifier(e.target.value)}
                className="form-control-sm"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label htmlFor="requirement-description">{`${I18n.get('description')} (${I18n.get('optional')})`}</Form.Label>
              <Form.Control
                id="requirement-description"
                as="textarea"
                rows={3}
                value={description}
                placeholder={I18n.get('enterField', { field: I18n.get('description') })}
                aria-label={I18n.get('enterField', { field: I18n.get('description') })}
                onChange={(e) => setDescription(e.target.value)}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleClose}>
            {I18n.get('cancel')}
          </Button>
          <Button variant="primary" onClick={() => void handleSubmit()}>
            {I18n.get('create')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
